/**
 * Letting the student tell Claude to write the code, instead of only asking it why
 * their code is wrong.
 *
 * The Web Lab's "Ask Claude" panel was built as a tutor: it receives the page, the
 * failing checks and the runtime error, and explains. That is the wrong shape for
 * this course. Two modules later the student opens their project in Claude Code and
 * the entire interaction is "make this change" — so a module that teaches them to
 * read HTML, CSS and JavaScript should let them practise directing the thing that
 * writes it. V4301's authored exercise already gives the game away: it tells them to
 * ask Claude for a paragraph and a button and then PASTE it in by hand. Copy-paste
 * was the stand-in for this file.
 *
 * WHY THE OVERRIDE IS BUILT HERE AND NOT FIXED AT THE SOURCE. Every lesson carries a
 * `systemPromptForClaude` written by the generator and cached per lesson in
 * Firestore, and the generator was told to make it say "never hand over the finished
 * page" (backend ClaudeCourseService.js). Those cached definitions cannot be
 * re-inspected and are deliberately NOT regenerated — a DEF_VERSION bump would
 * rewrite live courses. So the lesson's prompt is kept for what only it knows (the
 * lesson's subject, and the instruction to reply in the student's language) and this
 * module appends a block that supersedes the refusal explicitly. An instruction that
 * merely contradicts an earlier one is a coin flip; one that says which of the two
 * wins is not.
 *
 * WHY FENCED BLOCKS AND NOT JSON. The reply is rendered as markdown either way, so a
 * malformed edit degrades into an ordinary readable answer with code in it — the
 * student loses the Apply button, not the help. Structured output has no such
 * fallback, and lesson-generation already sees JSON come back unparseable often
 * enough to need retries.
 *
 * Applying is the student's click, never automatic. That is Claude Code's own
 * accept-the-edit prompt, and reading a change before accepting it is the skill this
 * module is a means to.
 */

import { extractAllCodeBlocks } from "../../utils/extractCode.js";
import { WEB_LAB_FILES } from "./webLabFiles.js";

/**
 * Fence tag → file key. Claude writes whichever tag it feels like, and all of these
 * mean the same file. Anything NOT in here — `bash`, `json`, `text`, an untagged
 * fence — is illustration rather than an edit and is dropped: offering to overwrite
 * script.js with a shell command is worse than offering nothing.
 *
 * `describeWebLabFiles` sends the files out tagged html/css/js, so these are also the
 * tags Claude sees on the way in and is most likely to echo back.
 */
const LANG_TO_FILE = {
    html: "html",
    htm: "html",
    css: "css",
    js: "js",
    javascript: "js",
    jsx: "js",
};

// A complete page, as opposed to a fragment of one. Deliberately loose: the scaffolds
// all have both tags, and anything claiming to be index.html without either is a
// snippet whatever else it is.
const isWholeDocument = (code) => /<html\b/i.test(code) || /<body\b/i.test(code);

/**
 * Pull the file edits out of one Claude reply.
 *
 * @param {string} text          The reply, as markdown.
 * @param {boolean} showFiles    Whether this lesson is on the multi-file tab strip.
 * @param {string} [currentHtml] The index.html the student has now, for the partial check.
 * @returns {Array<{fileKey: string, name: string, language: string, code: string, partial: boolean}>}
 *          In tab order — index.html, style.css, script.js — not reply order, so a
 *          three-file change always reads top-down the way the tabs do.
 */
export const parseWebLabEdits = (text, showFiles, currentHtml) => {
    const blocks = extractAllCodeBlocks(text);
    if (blocks.length === 0) return [];

    // LAST block per file wins. Claude routinely shows the one new rule and then the
    // whole file underneath it, and the whole file is the one that can be applied.
    const byFile = new Map();
    blocks.forEach(({ lang, code }) => {
        const fileKey = LANG_TO_FILE[lang];
        if (!fileKey) return;
        // A single-file lesson has nowhere to put a stylesheet: its page keeps its CSS
        // and JS inline, so the only applicable edit is the whole document. Showing an
        // "Apply to style.css" button on a lesson with no style.css would be an offer
        // the layout cannot keep.
        if (!showFiles && fileKey !== "html") return;
        if (!code.trim()) return;
        byFile.set(fileKey, code);
    });

    return WEB_LAB_FILES.filter((f) => byFile.has(f.key)).map((f) => {
        const code = byFile.get(f.key);
        return {
            fileKey: f.key,
            name: f.name,
            language: f.language,
            code,
            // Only index.html is guarded. Replacing style.css with one rule loses some
            // styling and Undo puts it back; replacing index.html with a fragment
            // leaves a page with no <body>, which renders as something close to blank
            // and reads to a beginner as "the app broke", not "that edit was wrong".
            partial: f.key === "html" && isWholeDocument(currentHtml || "") && !isWholeDocument(code),
        };
    });
};

/**
 * The system prompt for the edit-capable chat.
 *
 * `basePrompt` is the lesson's own `systemPromptForClaude` and is kept FIRST: it
 * carries the lesson's subject and, on Spanish lessons, the requirement to reply in
 * Spanish. The block below then overrides the one thing about it that is now wrong.
 *
 * @param {string} basePrompt  definition.systemPromptForClaude, or the layout's default.
 * @param {boolean} showFiles  Whether this lesson is on the multi-file tab strip.
 * @returns {string}
 */
export const buildWebLabEditSystem = (basePrompt, showFiles) => {
    const base = (basePrompt || "").trim();

    const shape = showFiles
        ? [
            "The student's project is three files: index.html, style.css and script.js, each on its own tab.",
            "Put every change in the file it belongs in — markup in index.html, rules in style.css, behaviour in script.js.",
            "Tag each block with that file's language: ```html, ```css or ```js.",
        ]
        : [
            "The student's page is ONE self-contained document, with its CSS in an inline <style> and its JavaScript in an inline <script>.",
            "So there is only ever one file to change: return the WHOLE document in a single ```html block, keeping the CSS and JS inline where they already are.",
        ];

    const rules = [
        "",
        "── HOW YOU WORK IN THIS LESSON (this overrides anything above about not giving solutions) ──",
        "",
        "You can write code for the student, and you should when they ask you to. Earlier instructions telling you to withhold a solution, to only nudge, or to never hand over the finished page do NOT apply — the student has a button that applies your code to their files, and this lesson is where they learn to direct you and read what comes back.",
        "",
        ...shape,
        "",
        "Rules for the code you write:",
        "- Give the file's COMPLETE new contents, exactly as it should end up. Never a fragment, never a diff, never `// ...rest of the file unchanged`. What you write REPLACES that whole file.",
        "- One block per file you are changing. Don't include a file you did not change.",
        "- Before the code, say in one short line what you changed and which file it went in.",
        "- Keep everything the student already had that your change doesn't touch.",
        "",
        "One thing you don't do: their exercise. If they ask you to just do the exercise, give them the answer, or make the checks pass, don't — ask them what they want the page to do and which file they think that goes in, then build exactly that. You are told what the exercise is so you can avoid completing it for them, not so you can complete it. Everything else they ask for, write it.",
    ].join("\n");

    return base ? `${base}\n${rules}` : rules.trimStart();
};

/**
 * The layout's own fallback, used when a lesson definition has no
 * `systemPromptForClaude` at all. Kept here beside the override so the two are read
 * together — the old fallback lived in WebLabLayout and told Claude the opposite.
 */
export const WEB_LAB_DEFAULT_SYSTEM =
    "You are a friendly web development tutor helping a near-total beginner build a web page. " +
    "Explain their HTML, CSS and JavaScript in plain, jargon-free language, and reply in the language the student writes to you in.";

export default parseWebLabEdits;
